import { z } from 'zod';
/**
 * Funis da clínica (Leads na mesa; estudo da mesa aprovado em 22/10). A lógica é a do
 * APOLO (`funnel_stages`, `contact_funnel_columns`, `persons.leadStatus`): etapas com
 * prazo em dias e pré-aviso, cartão com estrelas e responsável, perda com motivo, venda
 * e retomar. Diferença de propósito: nada fica escrito no código além do padrão; a
 * clínica cria funis, colunas e prazos.
 */
const isoDateTime = z.iso.datetime();
export const CLINIC_FUNNEL_NAME_MAX = 40;
export const CLINIC_FUNNEL_COLUMN_MAX = 40;
export const CLINIC_FUNNEL_COLUMNS_MAX = 16;
export const CLINIC_FUNNEL_NOTE_MAX = 300;
/** O que a coluna faz além de guardar cartões. */
export const CLINIC_FUNNEL_COLUMN_KINDS = ['etapa', 'agendado', 'venda'];
export const clinicFunnelColumnKindSchema = z.enum(CLINIC_FUNNEL_COLUMN_KINDS);
/**
 * Colunas padrão de um funil novo, com os prazos do APOLO (`storage.ts`, etapas do
 * funil): sem contato vence no mesmo dia, sem resposta em 30, abordagem em 1,
 * interessado em 7; agendado não vence; tratamento iniciado em 30.
 */
export const CLINIC_DEFAULT_FUNNEL_COLUMNS = [
    { name: 'Sem contato', kind: 'etapa', slaDays: 0 },
    { name: 'Sem resposta', kind: 'etapa', slaDays: 30 },
    { name: 'Abordagem', kind: 'etapa', slaDays: 1 },
    { name: 'Interessado', kind: 'etapa', slaDays: 7 },
    { name: 'Agendado', kind: 'agendado', slaDays: null },
    { name: 'Tratamento iniciado', kind: 'venda', slaDays: 30 },
];
/** Dias antes do vencimento em que o cartão fica em alerta (padrão do APOLO). */
export const CLINIC_DEFAULT_PRE_ALERT_DAYS = 2;
/** Motivos de perda do APOLO (`leads-aggregations.ts`), na escrita da clínica. */
export const CLINIC_LOSS_REASONS = [
    'Sem resposta',
    'Sem interesse',
    'Sem condições financeiras',
    'Achou caro',
    'Plano de saúde',
    'Concorrente',
    'Mora distante',
    'Não era o que procurava',
    'Buscava receita',
    'Telefone incorreto',
    'Cadastro duplicado',
    'Dispensado',
    'Faleceu',
    'SPAM',
    'Outro',
];
export const clinicLossReasonSchema = z.enum(CLINIC_LOSS_REASONS);
export const clinicFunnelColumnSchema = z.object({
    id: z.uuid(),
    funnelId: z.uuid(),
    name: z.string(),
    kind: clinicFunnelColumnKindSchema,
    sort: z.number().int().min(0),
    /** Dias para vencer (0 = no mesmo dia; null = não vence). */
    slaDays: z.number().int().min(0).max(365).nullable(),
    preAlertDays: z.number().int().min(0).max(60),
});
export const clinicFunnelSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    sort: z.number().int().min(0),
    archived: z.boolean(),
    columns: z.array(clinicFunnelColumnSchema),
    /** Cartões em aberto (para o número na aba). */
    openCount: z.number().int().min(0),
});
export const CLINIC_FUNNEL_CARD_STATUSES = ['aberto', 'perdido', 'ganho'];
export const clinicFunnelCardStatusSchema = z.enum(CLINIC_FUNNEL_CARD_STATUSES);
/** Prazo do cartão na coluna em que está agora. */
export const CLINIC_SLA_STATES = ['sem_prazo', 'ok', 'alerta', 'vencido'];
export const clinicFunnelCardSchema = z.object({
    id: z.uuid(),
    funnelId: z.uuid(),
    columnId: z.uuid(),
    patientId: z.uuid(),
    patientName: z.string(),
    phone: z.string().nullable(),
    origin: z.string().nullable(),
    /** Qualificação de 1 a 5 (1–2 frio, 3 morno, 4–5 quente), como no APOLO. */
    stars: z.number().int().min(1).max(5),
    ownerId: z.uuid().nullable(),
    ownerName: z.string().nullable(),
    status: clinicFunnelCardStatusSchema,
    lossReason: z.string().nullable(),
    note: z.string(),
    enteredAt: isoDateTime,
    dueAt: isoDateTime.nullable(),
    sla: z.enum(CLINIC_SLA_STATES),
    nextAppointmentAt: isoDateTime.nullable(),
    closedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const clinicFunnelBoardSchema = z.object({
    funnel: clinicFunnelSchema,
    cards: z.array(clinicFunnelCardSchema),
});
export const clinicFunnelEventSchema = z.object({
    id: z.uuid(),
    text: z.string(),
    actor: z.string(),
    at: isoDateTime,
});
// ── Entradas ────────────────────────────────────────────────────────────────
const name = (max) => z.string().trim().min(1).max(max);
export const createClinicFunnelSchema = z.object({ name: name(CLINIC_FUNNEL_NAME_MAX) });
export const updateClinicFunnelSchema = z
    .object({ name: name(CLINIC_FUNNEL_NAME_MAX), archived: z.boolean() })
    .partial();
export const createClinicFunnelColumnSchema = z.object({
    name: name(CLINIC_FUNNEL_COLUMN_MAX),
    slaDays: z.number().int().min(0).max(365).nullable().optional(),
    preAlertDays: z.number().int().min(0).max(60).optional(),
});
export const updateClinicFunnelColumnSchema = z
    .object({
    name: name(CLINIC_FUNNEL_COLUMN_MAX),
    slaDays: z.number().int().min(0).max(365).nullable(),
    preAlertDays: z.number().int().min(0).max(60),
    /** Nova posição (0 = primeira). */
    sort: z.number().int().min(0).max(CLINIC_FUNNEL_COLUMNS_MAX),
})
    .partial();
export const createClinicFunnelCardSchema = z
    .object({
    patientId: z.uuid().optional(),
    /** Lead novo: vira paciente com estado "lead". */
    name: z.string().trim().min(1).max(80).optional(),
    phone: z.string().trim().max(32).optional(),
    origin: z.string().trim().max(60).optional(),
    columnId: z.uuid().optional(),
    stars: z.number().int().min(1).max(5).optional(),
    note: z.string().trim().max(CLINIC_FUNNEL_NOTE_MAX).optional(),
})
    .refine((value) => value.patientId !== undefined || value.name !== undefined, {
    message: 'Diga quem é o lead.',
});
export const updateClinicFunnelCardSchema = z
    .object({
    /** Outro quadro (rodada 15): vai para a etapa `columnId` dele, ou para a primeira. */
    funnelId: z.uuid(),
    columnId: z.uuid(),
    stars: z.number().int().min(1).max(5),
    ownerId: z.uuid().nullable(),
    note: z.string().trim().max(CLINIC_FUNNEL_NOTE_MAX),
})
    .partial();
export const loseClinicFunnelCardSchema = z.object({
    reason: clinicLossReasonSchema,
    note: z.string().trim().max(CLINIC_FUNNEL_NOTE_MAX).optional(),
});
export const winClinicFunnelCardSchema = z.object({
    note: z.string().trim().max(CLINIC_FUNNEL_NOTE_MAX).optional(),
});
/**
 * Prazo do cartão: vence `slaDays` dias depois de entrar na coluna (0 = no fim do dia
 * em que entrou), com alerta `preAlertDays` antes (ou na metade, se não couber). Pura, para o servidor e para a tela.
 */
export function funnelSla(enteredAt, column, now, endOfDay) {
    if (column.slaDays === null)
        return { dueAt: null, state: 'sem_prazo' };
    const dueAt = column.slaDays === 0
        ? endOfDay(enteredAt)
        : new Date(enteredAt.getTime() + column.slaDays * 86_400_000);
    if (now >= dueAt)
        return { dueAt, state: 'vencido' };
    // Pré-aviso maior que o prazo (1 dia com aviso de 2) deixaria o cartão amarelo desde o
    // primeiro minuto: nesse caso o alerta acende na metade do prazo.
    const alertDays = column.preAlertDays < column.slaDays ? column.preAlertDays : column.slaDays / 2;
    const alertAt = new Date(dueAt.getTime() - alertDays * 86_400_000);
    return { dueAt, state: column.slaDays > 0 && now >= alertAt ? 'alerta' : 'ok' };
}
/**
 * Números do funil (rodada 15). Contados por quem entrou no período (a coorte), para a
 * conversão nunca passar de 100%: novos = cartões criados no período; contatados = os
 * que saíram da primeira etapa ou já fecharam; agendados e compareceram = com consulta
 * marcada depois de entrar; vendidos e perdidos = como estão hoje. "Vendas fechadas"
 * conta as vendas pela data, de qualquer coorte.
 */
export const clinicFunnelStatsQuerySchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
const funnelNumbers = z.object({
    created: z.number().int().min(0),
    contacted: z.number().int().min(0),
    scheduled: z.number().int().min(0),
    attended: z.number().int().min(0),
    won: z.number().int().min(0),
    lost: z.number().int().min(0),
    /** Vendidos ÷ novos, em porcentagem inteira; null sem novos. */
    conversion: z.number().int().nullable(),
    /** Perdidos ÷ (vendidos + perdidos); null sem fechados. */
    lossRate: z.number().int().nullable(),
});
export const clinicFunnelStatsSchema = z.object({
    from: z.string(),
    to: z.string(),
    current: funnelNumbers,
    /** O período de mesmo tamanho logo antes. */
    previous: funnelNumbers,
    /** Vendas fechadas no período (pela data da venda, de quem entrou quando for). */
    closedWon: z.number().int().min(0),
    lossReasons: z.array(z.object({ reason: z.string(), count: z.number().int().min(1) })),
    byOrigin: z.array(z.object({
        origin: z.string(),
        created: z.number().int().min(0),
        scheduled: z.number().int().min(0),
        won: z.number().int().min(0),
    })),
    /** Agora, por etapa: em aberto, com prazo vencido e dias médios na etapa. */
    stages: z.array(z.object({
        columnId: z.uuid(),
        name: z.string(),
        open: z.number().int().min(0),
        overdue: z.number().int().min(0),
        avgDays: z.number().nullable(),
    })),
});
